import React, { useState, useEffect } from 'react';
import { 
  Table, Button, Card, Empty, Space, Drawer, Typography, Tag, 
  Row, Col, Form, Input, Select, Switch, Divider, 
  Spin, Popconfirm, message, Tooltip, Modal, Tabs, List,
  InputNumber, DatePicker, TimePicker, Radio, Collapse
} from 'antd';
import { 
  PlusOutlined, EditOutlined, DeleteOutlined, 
  ExclamationCircleOutlined, PlayCircleOutlined, PauseCircleOutlined,
  SearchOutlined, SyncOutlined, SettingOutlined,
  ArrowUpOutlined, ArrowDownOutlined, PlusCircleOutlined,
  CloseCircleOutlined, InfoCircleOutlined, HistoryOutlined,
  CopyOutlined
} from '@ant-design/icons';
import { useAuth } from '../utils/auth';
import api from '../services/api';
import sopService from '../services/sopService';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;
const { Panel } = Collapse;
const { RangePicker } = DatePicker;

// 获取标签服务
const tagService = {
  // 获取标签列表
  getTags: async () => {
    try {
      const response = await api.get('/tags');
      console.log('获取标签列表响应:', response.data);
      return response.data.content || response.data;
    } catch (error) {
      console.error('获取标签列表错误:', error);
      throw error;
    }
  }
};

const SopTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tags, setTags] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const { tenant } = useAuth();
  const navigate = useNavigate();
  
  // 排序状态
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // 目标类型选项
  const targetTypes = [
    { value: 'CUSTOMER', label: '客户' },
    { value: 'GROUP', label: '客户群' }
  ];
  
  // 触发类型选项
  const triggerTypes = [
    { value: 'TAG_CHANGE', label: '标签变更' },
    { value: 'MANUAL', label: '手动触发' }
  ];
  
  // 步骤类型选项
  const stepTypes = [
    { value: 'MESSAGE', label: '发送消息' },
    { value: 'TAG', label: '更新标签' }
  ];
  
  // 获取SOP模板列表
  const fetchTemplates = async (page = pagination.current - 1, pageSize = pagination.pageSize) => {
    try {
      setLoading(true);
      
      // 从本地存储获取租户ID
      const tenantStr = localStorage.getItem('tenant');
      let tenantId;
      
      try {
        if (tenantStr) {
          const tenantObj = JSON.parse(tenantStr);
          tenantId = tenantObj.id;
        } else {
          throw new Error('找不到租户信息');
        }
      } catch (error) {
        console.error('解析tenant信息失败:', error);
        throw new Error('租户信息解析失败，无法继续操作');
      }
      
      // 构建请求参数
      const params = {
        page,
        size: pageSize,
        tenantId,
        sortBy,
        sortOrder
      };
      
      // 添加搜索参数
      if (searchText) {
        params.name = searchText;
      }
      
      // 获取模板列表
      const result = await sopService.getTemplates(params);
      
      // 处理响应数据
      let templateList = [];
      let total = 0;
      
      if (Array.isArray(result)) {
        templateList = result;
        total = result.length;
      } else if (result && typeof result === 'object') {
        if (Array.isArray(result.content)) {
          templateList = result.content;
          total = result.totalElements || result.content.length;
        }
      }
      
      setTemplates(templateList);
      setPagination({
        current: page + 1,
        pageSize,
        total
      });
      
    } catch (error) {
      console.error('获取智能体列表失败:', error);
      message.error('获取智能体列表失败');
    } finally {
      setLoading(false);
    }
  };
  
  // 获取标签列表
  const fetchTags = async () => {
    try {
      const tags = await tagService.getTags();
      setTags(tags);
    } catch (error) {
      console.error('获取标签列表失败:', error);
      message.error('获取标签列表失败');
    }
  };
  
  // 组件挂载时获取数据
  useEffect(() => {
    fetchTemplates();
    fetchTags();
  }, []);
  
  // 当排序参数变化时重新获取数据
  useEffect(() => {
    if (tenant?.id) {
      fetchTemplates();
    }
  }, [sortBy, sortOrder]);
  
  // 处理表格变化（包括分页和排序）
  const handleTableChange = (pagination, filters, sorter) => {
    // 处理排序
    if (sorter && sorter.field) {
      const field = sorter.field;
      let order = 'desc';  // 默认降序
      
      if (sorter.order === 'ascend') {
        order = 'asc';
      } else if (sorter.order === 'descend') {
        order = 'desc';
      }
      
      setSortBy(field);
      setSortOrder(order);
    }
    
    // 处理分页
    fetchTemplates(pagination.current - 1, pagination.pageSize);
  };
  
  // 处理搜索
  const handleSearch = (value) => {
    setSearchText(value);
    fetchTemplates(0, pagination.pageSize);
  };
  
  // 修改创建操作为导航到新页面
  const handleCreateTemplate = () => {
    navigate('/sop-templates/new');
  };
  
  // 修改编辑操作为导航到新页面
  const handleEditTemplate = (record) => {
    navigate(`/sop-templates/${record.id}`);
  };
  
  // 复制模板
  const handleCopyTemplate = async (record) => {
    try {
      setLoading(true);
      
      // 从本地存储获取租户ID
      const tenantStr = localStorage.getItem('tenant');
      let tenantId;
      
      try {
        if (tenantStr) {
          const tenantObj = JSON.parse(tenantStr);
          tenantId = tenantObj.id;
        } else {
          throw new Error('找不到租户信息');
        }
      } catch (error) {
        console.error('解析tenant信息失败:', error);
        throw new Error('租户信息解析失败，无法继续操作');
      }
      
      // 修改代码生成逻辑，确保不超过50个字符
      // 使用更短的时间戳格式，限制原始code的长度
      const originalCode = record.code || record.id.toString();
      const timestamp = dayjs().format('MMDDHHmm'); // 使用较短的时间戳格式：月日时分
      
      // 先去掉已有的 _copy_ 后缀，避免递归复制时code越来越长
      const baseCode = originalCode.replace(/_copy_\d{8}$/, '');
      
      // 计算可用于基础代码的最大长度 (50字符限制减去"_copy_"和时间戳的长度)
      const maxBaseCodeLength = 50 - 6 - timestamp.length;
      
      // 截取合适长度的基础代码
      const truncatedCode = baseCode.substring(0, maxBaseCodeLength);
      const newCode = `${truncatedCode}_copy_${timestamp}`;
      
      const newName = `${record.name} 副本`;
      
      await sopService.copySop(record.id, {
        newTemplateCode: newCode,
        newTemplateName: newName
      }, tenantId);
      
      message.success('智能体复制成功');
      fetchTemplates();
    } catch (error) {
      console.error('复制智能体失败:', error);
      message.error('复制智能体失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };
  
  // 删除模板
  const handleDeleteTemplate = async (record) => {
    try {
      setLoading(true);
      
      // 从本地存储获取租户ID
      const tenantStr = localStorage.getItem('tenant');
      let tenantId;
      
      try {
        if (tenantStr) {
          const tenantObj = JSON.parse(tenantStr);
          tenantId = tenantObj.id;
        } else {
          throw new Error('找不到租户信息');
        }
      } catch (error) {
        console.error('解析tenant信息失败:', error);
        throw new Error('租户信息解析失败，无法继续操作');
      }
      
      // 先检查模板是否有执行实例 - 仍保留此检查，用于显示警告
      const executionResult = await sopService.checkHasExecutions(record.id, tenantId);
      setLoading(false);
      
      const hasExecutions = executionResult && executionResult.hasExecutions;
      
      // 根据是否有执行实例，显示不同的确认对话框
      Modal.confirm({
        title: '确认删除',
        icon: <ExclamationCircleOutlined />,
        content: (
          <div>
            <p>确定要删除此智能体吗？</p>
            {hasExecutions && (
              <div>
                <Divider style={{ margin: '12px 0' }} />
                <Text type="danger" strong>警告：此智能体有正在运行的执行实例！</Text>
                <p>删除此智能体将同时停止所有相关的执行实例，这些实例将无法继续执行。</p>
                <p>如果您只是想暂停使用此智能体，建议使用"禁用"功能而不是删除。</p>
              </div>
            )}
          </div>
        ),
        onOk: async () => {
          try {
            setLoading(true);
            // 第三个参数表示是否停止执行实例
            await sopService.deleteTemplate(record.id, tenantId, true);
            
            message.success(hasExecutions 
              ? '智能体及其执行实例已成功删除' 
              : '智能体删除成功');
              
            fetchTemplates();
          } catch (error) {
            console.error('删除智能体失败:', error);
            if (error.response?.data?.message?.includes('foreign key constraint')) {
              message.error('删除失败：此智能体有关联的执行实例，请先停止这些实例');
            } else {
              message.error('删除智能体失败: ' + (error.response?.data?.message || error.message));
            }
          } finally {
            setLoading(false);
          }
        },
        onCancel() {
          message.info('已取消删除操作');
        },
        okText: hasExecutions ? '删除智能体并停止所有执行实例' : '确认删除',
        cancelText: '取消',
        okButtonProps: { 
          danger: true,
          ...(hasExecutions && { type: 'primary' })
        },
      });
    } catch (error) {
      console.error('检查智能体执行实例失败:', error);
      message.error('检查智能体执行实例状态失败');
      setLoading(false);
    }
  };
  
  // 激活/禁用模板
  const handleToggleStatus = async (record, activate) => {
    try {
      setLoading(true);
      
      // 从本地存储获取租户ID
      const tenantStr = localStorage.getItem('tenant');
      let tenantId;
      
      try {
        if (tenantStr) {
          const tenantObj = JSON.parse(tenantStr);
          tenantId = tenantObj.id;
        } else {
          throw new Error('找不到租户信息');
        }
      } catch (error) {
        console.error('解析tenant信息失败:', error);
        throw new Error('租户信息解析失败，无法继续操作');
      }
      
      if (activate) {
        await sopService.activateTemplate(record.id, tenantId);
        message.success('智能体已激活');
      } else {
        await sopService.deactivateTemplate(record.id, tenantId);
        message.success('智能体已禁用');
      }
      
      fetchTemplates();
    } catch (error) {
      console.error(`${activate ? '激活' : '禁用'}智能体失败:`, error);
      message.error(`${activate ? '激活' : '禁用'}智能体失败`);
    } finally {
      setLoading(false);
    }
  };
  
  // 表格列定义
  const columns = [
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <span>{text}</span>
          {record.status && record.status.toUpperCase() === 'ACTIVE' && <Tag color="green">已激活</Tag>}
        </Space>
      )
    },
    {
      title: '目标类型',
      dataIndex: 'targetType',
      key: 'targetType',
      render: (text) => {
        const target = targetTypes.find(t => t.value === text);
        return target ? target.label : text;
      }
    },
    {
      title: '步骤数',
      key: 'stepsCount',
      dataIndex: 'stepsCount',
      align: 'center',
      render: (text) => text || 0
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: true,
      sortOrder: sortBy === 'createdAt' ? (sortOrder === 'asc' ? 'ascend' : 'descend') : null,
      render: (text) => text ? dayjs(text).format('YYYY-MM-DD HH:mm') : '-'
    },
    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      sorter: true,
      sortOrder: sortBy === 'updatedAt' ? (sortOrder === 'asc' ? 'ascend' : 'descend') : null,
      render: (text) => text ? dayjs(text).format('YYYY-MM-DD HH:mm') : '-'
    },
    {
      title: '操作',
      key: 'action',
      width: 200, // 加宽操作列以适应新增的复制按钮
      render: (_, record) => {
        // 根据不同状态设置按钮的可用性
        // 确保将状态转为大写后再比较
        const status = (record.status || '').toUpperCase();
        const isActive = status === 'ACTIVE';
        
        return (
        <Space size="small">
          <Button 
            type="link" 
            onClick={() => handleEditTemplate(record)}
            title={isActive ? "进入查看模式，激活状态的智能体不能保存修改" : "编辑智能体"}
          >
            {isActive ? "查看" : "编辑"}
          </Button>
          
          <Button
            type="link"
            onClick={() => navigate(`/sop-templates/${record.id}`, { state: { tab: 'executionStatus' } })}
            title="查看执行情况"
          >
            执行情况
          </Button>
          
          <Button 
            type="link"
            onClick={() => handleCopyTemplate(record)}
            title="复制模板"
          >
            复制
          </Button>
          
          {isActive ? (
            <Button 
              type="link"
              onClick={() => handleToggleStatus(record, false)}
            >
              禁用
            </Button>
          ) : (
            <Button 
              type="link" 
              onClick={() => handleToggleStatus(record, true)}
            >
              激活
            </Button>
          )}
          
          {isActive ? (
            // 激活状态下只显示禁用的删除按钮，不带Popconfirm
            <Button 
              type="link"
              danger 
              disabled={true}
              title="激活状态的智能体不能删除，请先禁用"
            >
              删除
            </Button>
          ) : (
            // 非激活状态下可以删除，直接调用handleDeleteTemplate
            <Button 
              type="link"
              danger 
              onClick={() => handleDeleteTemplate(record)}
            >
              删除
            </Button>
          )}
        </Space>
      )},
    },
  ];
  
  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={5}>智能体</Title>
        </Col>
      </Row>

      <Card>
        <div style={{ marginBottom: 16 }}>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col>
              <Space>
                <Input.Search
                  placeholder="搜索智能体"
                  allowClear
                  enterButton
                  onSearch={handleSearch}
                  style={{ width: 250 }}
                />
              </Space>
            </Col>
          </Row>
          <Row>
            <Col>
              <Space>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleCreateTemplate}
                >
                  新建智能体
                </Button>
              </Space>
            </Col>
          </Row>
        </div>
        <Table
          columns={columns}
          dataSource={templates}
          rowKey="id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
        />
      </Card>
    </div>
  );
};

export default SopTemplates; 